// Custom hover tooltip. We don't rely on the Wowhead Power widget anymore
// because its localised tooltip data for some Vanilla classic spells is
// missing (the popup renders "Spell未找到"). We have our own /api/wow/lookup
// scraping that returns name + icon + description for both spells and items
// reliably, so we render our own popup off that.

import { iconNameToUrl, lookup } from './wowhead';

let popup: HTMLDivElement | null = null;
let currentToken = 0;

function ensurePopup(): HTMLDivElement {
  if (popup) return popup;
  popup = document.createElement('div');
  popup.id = 'custom-tooltip';
  popup.setAttribute('role', 'tooltip');
  document.body.appendChild(popup);
  return popup;
}

function position(el: HTMLDivElement, x: number, y: number): void {
  // Try right side of cursor first, fall back to left if there isn't room.
  const pad = 16;
  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = x + pad;
  let top = y + pad;
  if (left + rect.width > vw - 8) left = Math.max(8, x - pad - rect.width);
  if (top + rect.height > vh - 8) top = Math.max(8, vh - rect.height - 8);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

// Wowhead's quality class names (q0=poor, q5=legendary). We re-use them on
// the tooltip name row so the colour matches the gear table border / link
// colour the user already sees in the Gear panel.
function qualityClass(quality: number | undefined): string {
  if (quality == null) return '';
  if (quality >= 0 && quality <= 5) return `q${quality}`;
  return '';
}

function render(
  el: HTMLDivElement,
  name: string | null,
  description: string | null,
  iconUrl: string | null,
  rawHtml: string | null,
  qClass: string,
): void {
  const iconHtml = iconUrl
    ? `<img class="ct-icon" src="${iconUrl}" alt="" width="36" height="36" />`
    : '';
  const nameClass = ['ct-name', qClass].filter(Boolean).join(' ');
  const nameHtml = name
    ? `<div class="${nameClass}">${escapeHtml(name)}</div>`
    : `<div class="${nameClass} muted">—</div>`;
  // Items: prefer the pre-rendered Wowhead tooltip HTML (real stats / effects).
  // Spells: fall back to plain text description.
  let bodyHtml = '';
  if (rawHtml) {
    bodyHtml = `<div class="ct-rich">${rawHtml}</div>`;
  } else if (description) {
    bodyHtml = `<div class="ct-desc">${escapeHtml(description).replace(/\n/g, '<br />')}</div>`;
  }
  el.innerHTML = `<div class="ct-head">${iconHtml}${nameHtml}</div>${bodyHtml}`;
}

export function attachTooltip(
  target: HTMLElement,
  type: 'spell' | 'item',
  id: number,
  quality?: number,
): void {
  if (!id) return;
  const el = ensurePopup();
  const qClass = qualityClass(quality);

  let lastX = 0;
  let lastY = 0;

  target.addEventListener('mouseenter', (e) => {
    lastX = e.clientX;
    lastY = e.clientY;
    currentToken += 1;
    const myToken = currentToken;
    el.classList.add('visible');
    render(el, '...', null, null, null, qClass);
    position(el, lastX, lastY);
    void lookup(type, id).then((r) => {
      if (myToken !== currentToken) return;
      const iconUrl = r.icon ? iconNameToUrl(r.icon) : null;
      render(el, r.name, r.description, iconUrl, r.tooltipHtml, qClass);
      position(el, lastX, lastY);
    });
  });
  target.addEventListener('mousemove', (e) => {
    lastX = e.clientX;
    lastY = e.clientY;
    if (el.classList.contains('visible')) position(el, lastX, lastY);
  });
  target.addEventListener('mouseleave', () => {
    currentToken += 1; // invalidate any in-flight lookup
    el.classList.remove('visible');
  });
}
